import {
  type FacilityHierarchyInput,
  getFacilityHierarchyId,
  mergeSessionHierarchyFields,
} from "@utils/facilityUtils";

export type FacilityNetworkRole = "parent" | "child" | "standalone";

export type FacilityNetworkViewModel = {
  role: FacilityNetworkRole;
  /** Top-level root in the network (no parent above). */
  anchor: FacilityHierarchyInput;
  /** All facilities in the network subtree under anchor (flat list, enriched). */
  members: FacilityHierarchyInput[];
  /** Ordered ancestors from root to immediate parent (excludes selected). */
  ancestors: FacilityHierarchyInput[];
  selected: FacilityHierarchyInput;
  isStandalone: boolean;
};

function isChildLinkedToParentGroup(
  child: FacilityHierarchyInput,
  parentGroupId: string,
): boolean {
  return child.parent_facility_group_id?.trim() === parentGroupId;
}

function sameFacilityId(
  a: FacilityHierarchyInput,
  b: FacilityHierarchyInput,
): boolean {
  const aId = getFacilityHierarchyId(a);
  const bId = getFacilityHierarchyId(b);
  return aId != null && bId != null && aId === bId;
}

function findFacilityById(
  facilities: FacilityHierarchyInput[],
  facilityId: number,
): FacilityHierarchyInput | undefined {
  const walk = (list: FacilityHierarchyInput[]): FacilityHierarchyInput | undefined => {
    for (const facility of list) {
      const id = getFacilityHierarchyId(facility);
      if (id === facilityId) return facility;
      const nested = walk(facility.child_facilities || []);
      if (nested) return nested;
    }
    return undefined;
  };
  return walk(facilities) ?? facilities.find((f) => getFacilityHierarchyId(f) === facilityId);
}

function mergeNestedChildrenForFacility<T extends FacilityHierarchyInput>(
  facility: T,
  byId: Map<number, T>,
  childrenByParentGroupId: Map<string, T[]>,
): T {
  const groupId = facility.facility_group_id?.trim();
  if (!groupId) return facility;

  const merged = new Map<number, FacilityHierarchyInput>();
  [...(facility.child_facilities || []), ...(childrenByParentGroupId.get(groupId) || [])].forEach(
    (child) => {
      const childId = getFacilityHierarchyId(child);
      if (childId == null) return;
      const enrichedChild = byId.get(childId) ?? child;
      if (!isChildLinkedToParentGroup(enrichedChild, groupId)) return;
      merged.set(childId, enrichedChild);
    },
  );

  const nestedChildren = Array.from(merged.values()).map((child) => {
    const childId = getFacilityHierarchyId(child);
    const base = (childId != null ? byId.get(childId) : undefined) ?? child;
    return mergeNestedChildrenForFacility({ ...base } as T, byId, childrenByParentGroupId);
  });

  return {
    ...facility,
    child_facilities: nestedChildren,
  };
}

function enrichFacilitiesWithNestedChildren<T extends FacilityHierarchyInput>(
  facilities: T[],
): T[] {
  const byId = new Map<number, T>();

  facilities.forEach((facility) => {
    const id = getFacilityHierarchyId(facility);
    if (id == null) return;
    byId.set(id, {
      ...facility,
      child_facilities: [...(facility.child_facilities || [])],
    });
  });

  const childrenByParentGroupId = new Map<string, T[]>();
  facilities.forEach((facility) => {
    const parentGroupId = facility.parent_facility_group_id?.trim();
    if (!parentGroupId) return;
    const id = getFacilityHierarchyId(facility);
    const enriched = id != null ? byId.get(id) ?? facility : facility;
    const children = childrenByParentGroupId.get(parentGroupId) || [];
    if (
      !children.some(
        (existing) =>
          getFacilityHierarchyId(existing) === getFacilityHierarchyId(enriched),
      )
    ) {
      children.push(enriched as T);
    }
    childrenByParentGroupId.set(parentGroupId, children);
  });

  byId.forEach((facility, facilityId) => {
    byId.set(
      facilityId,
      mergeNestedChildrenForFacility(facility, byId, childrenByParentGroupId),
    );
  });

  return facilities.map((facility) => {
    const id = getFacilityHierarchyId(facility);
    return id != null ? byId.get(id) ?? facility : facility;
  });
}

function getDirectChildren(
  parent: FacilityHierarchyInput,
  allFacilities: FacilityHierarchyInput[],
): FacilityHierarchyInput[] {
  const groupId = parent.facility_group_id?.trim();
  const childMap = new Map<number, FacilityHierarchyInput>();

  const addChild = (child: FacilityHierarchyInput) => {
    const id = getFacilityHierarchyId(child);
    if (id == null) return;
    if (groupId && !isChildLinkedToParentGroup(child, groupId)) return;
    childMap.set(id, child);
  };

  (parent.child_facilities || []).forEach(addChild);

  if (groupId) {
    allFacilities.forEach((facility) => {
      if (isChildLinkedToParentGroup(facility, groupId)) {
        addChild(facility);
      }
    });
  }

  return Array.from(childMap.values());
}

function collectAllInSubtree(
  root: FacilityHierarchyInput,
  allFacilities: FacilityHierarchyInput[],
): FacilityHierarchyInput[] {
  const result: FacilityHierarchyInput[] = [root];
  const seen = new Set<number>();
  const rootId = getFacilityHierarchyId(root);
  if (rootId != null) seen.add(rootId);

  const walk = (facility: FacilityHierarchyInput) => {
    getDirectChildren(facility, allFacilities).forEach((child) => {
      const childId = getFacilityHierarchyId(child);
      if (childId == null || seen.has(childId)) return;
      seen.add(childId);
      result.push(child);
      walk(child);
    });
  };

  walk(root);
  return result;
}

function buildAncestorChain(
  facility: FacilityHierarchyInput,
  allFacilities: FacilityHierarchyInput[],
): FacilityHierarchyInput[] {
  const ancestors: FacilityHierarchyInput[] = [];
  let current = facility;
  let parent = findParentForChild(current, allFacilities);
  while (parent) {
    ancestors.unshift(parent);
    current = parent;
    parent = findParentForChild(current, allFacilities);
  }
  return ancestors;
}

function findRootAncestor(
  facility: FacilityHierarchyInput,
  allFacilities: FacilityHierarchyInput[],
): FacilityHierarchyInput {
  const ancestors = buildAncestorChain(facility, allFacilities);
  return ancestors[0] ?? facility;
}

function collectChildrenForParent(
  parent: FacilityHierarchyInput,
  allFacilities: FacilityHierarchyInput[],
): FacilityHierarchyInput[] {
  const groupId = parent.facility_group_id?.trim();
  const childMap = new Map<number, FacilityHierarchyInput>();

  const addChild = (child: FacilityHierarchyInput) => {
    const id = getFacilityHierarchyId(child);
    if (id == null) return;
    if (groupId && !isChildLinkedToParentGroup(child, groupId)) return;
    childMap.set(id, child);
  };

  (parent.child_facilities || []).forEach(addChild);

  if (groupId) {
    allFacilities.forEach((facility) => {
      if (isChildLinkedToParentGroup(facility, groupId)) {
        addChild(facility);
      }
    });
  }

  return Array.from(childMap.values());
}

function findParentForChild(
  child: FacilityHierarchyInput,
  allFacilities: FacilityHierarchyInput[],
): FacilityHierarchyInput | undefined {
  const parentGroupId = child.parent_facility_group_id?.trim();
  if (!parentGroupId) return undefined;

  const walk = (list: FacilityHierarchyInput[]): FacilityHierarchyInput | undefined => {
    for (const facility of list) {
      if (facility.facility_group_id?.trim() === parentGroupId) return facility;
      const nested = walk(facility.child_facilities || []);
      if (nested) return nested;
    }
    return undefined;
  };

  return walk(allFacilities);
}

/**
 * Resolve the facility network visible for a selected facility.
 * Standalone facilities (no parent, no children) return a single-member view.
 */
export function resolveFacilityNetworkView(
  selected: FacilityHierarchyInput,
  allFacilities: FacilityHierarchyInput[],
): FacilityNetworkViewModel {
  const enrichedAll = enrichFacilitiesWithNestedChildren(allFacilities);
  const selectedId = getFacilityHierarchyId(selected);
  const normalizedSelected = mergeSessionHierarchyFields(
    (selectedId != null ? findFacilityById(enrichedAll, selectedId) : undefined) ??
      selected,
  );

  const ancestors = buildAncestorChain(normalizedSelected, enrichedAll);
  const parentFromGroup = ancestors[ancestors.length - 1];

  if (parentFromGroup) {
    const root = findRootAncestor(normalizedSelected, enrichedAll);
    const rootId = getFacilityHierarchyId(root);
    const normalizedRoot =
      rootId != null ? findFacilityById(enrichedAll, rootId) ?? root : root;
    const members = collectAllInSubtree(normalizedRoot, enrichedAll);
    const selectedInMembers =
      selectedId != null ? findFacilityById(members, selectedId) ?? normalizedSelected : normalizedSelected;

    return {
      role: sameFacilityId(selectedInMembers, normalizedRoot) ? "parent" : "child",
      anchor: normalizedRoot,
      members,
      ancestors,
      selected: selectedInMembers,
      isStandalone: false,
    };
  }

  if (normalizedSelected.parent_facility_group_id?.trim()) {
    return {
      role: "child",
      anchor: normalizedSelected,
      members: [normalizedSelected],
      ancestors: [],
      selected: normalizedSelected,
      isStandalone: false,
    };
  }

  const children = collectChildrenForParent(normalizedSelected, enrichedAll);
  if (children.length > 0) {
    return {
      role: "parent",
      anchor: normalizedSelected,
      members: collectAllInSubtree(normalizedSelected, enrichedAll),
      ancestors: [],
      selected: normalizedSelected,
      isStandalone: false,
    };
  }

  return {
    role: "standalone",
    anchor: normalizedSelected,
    members: [normalizedSelected],
    ancestors: [],
    selected: normalizedSelected,
    isStandalone: true,
  };
}

function countDescendants(facility: FacilityHierarchyInput): number {
  const children = facility.child_facilities || [];
  return children.reduce((sum, child) => sum + 1 + countDescendants(child), 0);
}

export function countDirectChildren(view: FacilityNetworkViewModel): number {
  const nested = view.selected.child_facilities || [];
  if (nested.length > 0) return nested.length;
  const selectedId = getFacilityHierarchyId(view.selected);
  const selectedGroupId = view.selected.facility_group_id?.trim();
  if (!selectedGroupId || selectedId == null) return 0;
  return view.members.filter(
    (member) =>
      member.parent_facility_group_id?.trim() === selectedGroupId &&
      getFacilityHierarchyId(member) !== selectedId,
  ).length;
}

export function countNetworkTotal(view: FacilityNetworkViewModel): number {
  const anchorId = getFacilityHierarchyId(view.anchor);
  let total = 1;
  view.members.forEach((member) => {
    if (getFacilityHierarchyId(member) === anchorId) {
      total += countDescendants(member);
    }
  });
  if (total === 1 && view.members.length > 1) {
    total = view.members.length;
  }
  return total;
}
